import { useState, useEffect, useCallback } from 'react';

import { createPortal } from 'react-dom';

import PropTypes from 'prop-types';

import Button from '#globalComponents/Button/Button';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';

import { useWzCodesTree } from '../../../models/wzCodes/queries';

import { WzCodeAutocomplete } from './WzCodeAutocomplete';
import { WzCodeTree } from './WzCodeTree';

/**
 * Collects a node's code + all descendant codes.
 */
const collectSelfAndDescendants = (code, childrenByParent, acc = []) => {
	acc.push(code);
	const children = childrenByParent.get(code) || [];
	for (const child of children) {
		collectSelfAndDescendants(child.code, childrenByParent, acc);
	}
	return acc;
};

export const WzCodePickerModal = ({ isOpen, onClose, onConfirm, initialSelection }) => {
	const { data, isLoading, isError } = useWzCodesTree();

	const [selectedSet, setSelectedSet] = useState(() => new Set());
	const [expandedSet, setExpandedSet] = useState(() => new Set());

	// Reset selection to initialSelection whenever the modal is (re)opened
	useEffect(() => {
		if (isOpen) {
			const initial = Array.isArray(initialSelection)
				? initialSelection.filter((c) => typeof c === 'string')
				: [];
			setSelectedSet(new Set(initial));
			setExpandedSet(new Set());
		}
	}, [isOpen, initialSelection]);

	const handleToggleCheck = useCallback(
		(node, shouldCheck) => {
			if (!data) return;
			const descendants = collectSelfAndDescendants(node.code, data.childrenByParent);
			setSelectedSet((prev) => {
				const next = new Set(prev);
				if (shouldCheck) {
					descendants.forEach((c) => next.add(c));
				} else {
					descendants.forEach((c) => next.delete(c));
				}
				return next;
			});
		},
		[data],
	);

	const handleToggleExpand = useCallback((code) => {
		setExpandedSet((prev) => {
			const next = new Set(prev);
			if (next.has(code)) next.delete(code);
			else next.add(code);
			return next;
		});
	}, []);

	const handleConfirm = () => {
		// Empty selection is valid — it clears any previously picked codes.
		// Returns a sorted array of WZ code strings.
		const sorted = [...selectedSet].sort((a, b) => a.localeCompare(b));
		onConfirm(sorted);
	};

	const selectedCount = selectedSet.size;

	if (!isOpen) return null;

	return createPortal(
		<Modal isOpen={isOpen} onClose={onClose} size="l">
			<Panel.Header onClose={onClose}>WZ-2025 Branchencodes auswählen</Panel.Header>
			<Panel.Body>
				<div className="mb-4">
					<p className="text-xs text-text mb-2">
						<strong>WZ-Codes</strong> sind die offizielle deutsche Klassifikation der Wirtschaftszweige
						(Ausgabe 2025) des Statistischen Bundesamts. Jedes im Handelsregister eingetragene Unternehmen
						ist einer oder mehreren dieser Branchen zugeordnet — die Codes sind hierarchisch aufgebaut, von
						groben Abschnitten (z.B. <span className="font-mono">C – Verarbeitendes Gewerbe</span>) bis hin
						zu sehr spezifischen Unterklassen (z.B.{' '}
						<span className="font-mono">
							28.41.0 – Herstellung von Werkzeugmaschinen für die Metallbearbeitung
						</span>
						).
					</p>
					<p className="text-xs text-text">
						Wählen Sie die gewünschten Codes manuell aus. Beim Anhaken einer Oberkategorie werden
						automatisch alle Unterkategorien mitausgewählt. Ist hier mindestens ein Code gesetzt,
						überschreibt er den Freitext der Branche.
					</p>
				</div>

				{(isLoading || !data || data.roots.length === 0) && !isError && (
					<div className="py-12 text-center text-sm text-bgBase-800">Lade WZ-Codes…</div>
				)}

				{isError && (
					<div className="py-6 px-4 bg-red-50 border border-red-200 rounded text-sm text-warning-700">
						Die WZ-Codes konnten nicht geladen werden. Bitte schließen Sie das Fenster und versuchen Sie es
						erneut.
					</div>
				)}

				{data && data.roots.length > 0 && !isLoading && (
					<>
						<div className="mb-3">
							<WzCodeAutocomplete
								nodes={data.nodes}
								selectedSet={selectedSet}
								onToggle={handleToggleCheck}
							/>
						</div>

						<div className="border border-bgBase-400 rounded-md max-h-[50vh] overflow-y-auto bg-white shadow-sm">
							<WzCodeTree
								roots={data.roots}
								childrenByParent={data.childrenByParent}
								selectedSet={selectedSet}
								expandedSet={expandedSet}
								onToggleCheck={handleToggleCheck}
								onToggleExpand={handleToggleExpand}
							/>
						</div>
					</>
				)}
			</Panel.Body>
			<Panel.Footer className="justify-end">
				<Button variant="outline" size="md" onClick={onClose}>
					Abbrechen
				</Button>
				<Button variant="solid" size="md" onClick={handleConfirm} disabled={!data}>
					Übernehmen {selectedCount > 0 ? `(${selectedCount})` : ''}
				</Button>
			</Panel.Footer>
		</Modal>,
		document.body,
	);
};

WzCodePickerModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	onConfirm: PropTypes.func.isRequired,
	initialSelection: PropTypes.arrayOf(PropTypes.string),
};

export default WzCodePickerModal;
